import React, { useMemo, useState } from 'react';
import axios from 'axios';
import getWebchatUrl from '@/utils/getWebchatUrl';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
	Select,
	SelectTrigger,
	SelectContent,
	SelectItem,
	SelectGroup,
	SelectLabel,
} from '@/components/ui/select';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import {
	Folder,
	User,
	Tag,
	FolderOpen,
	CircleCheck,
	ChevronDown,
	Loader2,
	MailWarning,
	MailCheck,
	CheckCheck,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedConversations } from '@/actions/userInterface';
import { MultiSelect } from '@/components/ui/custom/multi-select';
import { batchRefreshCustomerConversations } from '@/actions/webchat';
import { bulkUpdateCustomerTags } from '@/actions/tags';
import { getAllTags } from '@/actions/tags';

// 768-1279px: an icon-only MultiSelect trigger. The placeholder stays for
// screen readers, and !min-w-0 beats the component's inline min-width.
const MEDIUM_ICON_MULTI_SELECT_CLASS =
	'max-xl:!w-auto max-xl:!min-w-0 max-xl:min-h-9 max-xl:h-9 max-xl:px-2 max-xl:[&_span.ml-1]:sr-only max-xl:[&_svg]:mx-0 max-xl:[&>div]:gap-1';

/**
 * State and handlers for bulk actions on the selected inbox conversations.
 * The desktop ActionBar and the phone bulk toolbar share them.
 */
export const useBulkConversationActions = () => {
	const [selectedTags, setSelectedTags] = useState([]);
	const [selectedAssignee, setSelectedAssignee] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useDispatch();

	const { selectedConversations } = useSelector(
		(state: any) => state.userInterface.webchat
	);

	const users = useSelector((state: any) => state.users);

	const departments = useSelector((state: any) => state.departments);

	const allowedDepartments = useMemo(() => {
		if (!departments?.length) return [];
		if (!selectedConversations?.length) return [];

		return departments.filter((department: any) => {
			return (
				department.isActive &&
				department._business === selectedConversations[0]?._business
			);
		});
	}, [departments, selectedConversations]);

	// Memoized calculation of allowed businesses from selected conversations
	const allowedBusinesses = useMemo(() => {
		if (!selectedConversations?.length) return [];

		const businessSet = new Set();
		selectedConversations.forEach((conversation: any) => {
			if (conversation._business) {
				businessSet.add(conversation._business);
			}
		});

		return Array.from(businessSet);
	}, [selectedConversations]);

	const shouldShowOpenOrClosed = useMemo(() => {
		return selectedConversations.some(
			(conversation: any) => conversation.open
		);
	}, [selectedConversations]);

	// Memoized filtering of assignees based on allowed businesses
	const assignees = useMemo(() => {
		if (!users?.length || !allowedBusinesses.length) return [];

		const unassignedUserValue = {
			_id: 'UNASSIGNED',
			name: 'Unassigned',
		};

		const usersWithUnassigned = [unassignedUserValue, ...users];

		return usersWithUnassigned.filter((user) => {
			// Allow "Unassigned" value as a valid assignee

			if (user._id === 'UNASSIGNED') return true;

			if (!user.active || !user._businesses?.length) return false;

			// Check if user belongs to any of the allowed businesses
			return allowedBusinesses.some((business) =>
				user._businesses.includes(business)
			);
		});
	}, [users, allowedBusinesses]);

	const folders = useSelector((state: any) => state.folders);

	const tags = useSelector((state: { tags: string[] }) => state.tags);

	const convertedTags = tags.map((tag) => ({
		label: tag,
		value: tag,
	}));

	const [selectedFolder, setSelectedFolder] = useState(null);

	const conversations = useSelector(
		(state: any) => state.webchat.customerConversations
	);

	// Helper function to handle async actions with loading states
	const handleAsyncAction = async (asyncFunction: () => Promise<void>) => {
		try {
			setIsLoading(true);
			await asyncFunction();
		} catch (error) {
			console.error('Error performing action:', error);
			// You can add toast notifications or error handling here
		} finally {
			setIsLoading(false);
			onClear();
		}
	};

	const onClear = () => {
		dispatch(setSelectedConversations([]));
		setSelectedFolder(null);
		setSelectedAssignee(null);
		setSelectedTags([]);
	};

	const onMarkUnread = async () => {
		await handleAsyncAction(async () => {
			const _selectedConversations = selectedConversations.map(
				(conversation: any) => conversation._id
			);

			const res = await axios.post(
				`${getWebchatUrl(
					window.location.href
				)}/api/conversation/set-selected-conversations-isRead`,
				{
					_customerConversations: _selectedConversations,
					isRead: false,
				}
			);

			if (!res.data.error) {
				toast.success('Conversations marked as unread');
			} else {
				toast.error('Error marking conversations as unread');
			}
		});
	};

	const onMarkRead = async () => {
		await handleAsyncAction(async () => {
			const _selectedConversations = selectedConversations.map(
				(conversation: any) => conversation._id
			);

			const res = await axios.post(
				`${getWebchatUrl(
					window.location.href
				)}/api/conversation/set-selected-conversations-isRead`,
				{
					_customerConversations: _selectedConversations,
					isRead: true,
				}
			);

			if (!res.data.error) {
				toast.success('Conversations marked as read');
			} else {
				toast.error('Error marking conversations as read');
			}
		});
	};

	const onMarkOpen = async () => {
		await handleAsyncAction(async () => {
			const _selectedConversations = selectedConversations.map(
				(conversation: any) => conversation._id
			);

			const res = await axios.post(
				`${getWebchatUrl(
					window.location.href
				)}/api/conversation/set-selected-conversations-open`,
				{
					_customerConversations: _selectedConversations,
				}
			);

			if (!res.data.error) {
				toast.success('Conversations marked as open');
			} else {
				toast.error('Error marking conversations as open');
			}
		});
	};

	const onMarkClosed = async () => {
		await handleAsyncAction(async () => {
			// Replace this with your actual API call

			const res = await axios.post(
				`${getWebchatUrl(
					window.location.href
				)}/api/conversation/set-selected-conversations-closed`,
				{
					_customerConversations: selectedConversations.map(
						(conversation: any) => conversation._id
					),
				}
			);

			if (!res.data.error) {
				toast.success('Conversations marked as closed');
			} else {
				toast.error('Error marking conversations as closed');
			}
			// setSelectedConversationsClosed(selectedConversations);
		});
	};

	const onAddAssigneeToConversations = async (
		assignee: any,
		isDepartment: boolean
	) => {
		await handleAsyncAction(async () => {
			setSelectedAssignee(assignee);

			let _assignee = assignee._id;

			if (assignee._id === 'UNASSIGNED') {
				_assignee = 'Unassigned';
			}

			const res = await axios.post(
				`${getWebchatUrl(
					window.location.href
				)}/api/team/bulk-assign-conversations`,
				{
					_customerConversations: selectedConversations.map(
						(conversation: any) => conversation._id
					),
					_assignees:
						isDepartment || _assignee === 'Unassigned'
							? []
							: [_assignee],
					_assignedUser:
						isDepartment || _assignee === 'Unassigned'
							? _assignee
							: undefined,
					isDepartment: isDepartment,
					_business: selectedConversations[0]._business,
				}
			);

			if (!res.data.error) {
				toast.success('Conversations assigned to user');
			} else {
				toast.error('Error assigning conversations to user');
			}
		});
	};

	const onMoveConversationsToFolder = async (_folder: string) => {
		await handleAsyncAction(async () => {
			setSelectedFolder(_folder);
			// Replace this with your actual API call
			const res = await axios.post(
				`/api/folders/bulk-move-conversations-to-folder`,
				{
					_folder,
					_customerConversations: selectedConversations.map(
						(conversation: any) => conversation._id
					),
					_business: selectedConversations[0]._business,
				}
			);

			if (!res.data.error) {
				toast.success('Conversations moved to folder');
			} else {
				toast.error('Error moving conversations to folder');
			}
		});
	};

	// tagsOverride lets the phone tag sheet pass its picks directly instead of
	// through selectedTags, which would still hold the previous render's value.
	const onAddTagToConversations = async (createdTag?: string, tagsOverride?: string[]) => {
		await handleAsyncAction(async () => {
			try {
				// Extract unique customer IDs from selected conversations
				// (group conversations carry _customers — customer tags don't apply)
				const customerIds = [
					...new Set(
						selectedConversations
							.map((conversation: any) => conversation._customer?._id)
							.filter(Boolean)
					),
				];

				const baseTags = tagsOverride ?? selectedTags;
				const tagsToApply = createdTag
					? Array.from(new Set([...baseTags, createdTag]))
					: baseTags;

				// Use the proper action that calls the correct API and handles cache invalidation
				await dispatch(
					bulkUpdateCustomerTags({
						customerIds,
						tags: tagsToApply,
					})
				);

				toast.success('Tags added to conversations');

				// Batch refresh conversations to reflect updated customer data
				// This only refreshes conversations that are already loaded in Redux
				await dispatch(
					batchRefreshCustomerConversations({
						customerIds,
					})
				);

				await dispatch(getAllTags());
			} catch (error) {
				console.error('Error adding tags to conversations:', error);
				toast.error('Error adding tags to conversations');
			}
		});
	};

	// Memoized folder title lookup function
	const getFolderTitle = useMemo(() => {
		return (_folder: string) => {
			const folder = folders.find((f) => f._id === _folder);
			return folder ? folder.title : null;
		};
	}, [folders]);

	const onSelectAll = () => {
		if (selectedConversations.length === conversations.length) {
			dispatch(setSelectedConversations([]));
		} else {
			dispatch(
				setSelectedConversations(
					conversations.map((conversation: any) => conversation)
				)
			);
		}
	};

	const onAddAssigneesToConversations = async (selectedValues: string[]) => {
		await handleAsyncAction(async () => {
			// Determine if department or users based on selection
			// Logic: If any selection is a department, treat as department assignment (single)
			// If users, send all user IDs.

			let isDepartment = false;
			let _assignedUser = 'Unassigned';
			let _assignees: string[] = [];

			const departmentSelection = selectedValues.find((v) =>
				v.startsWith('DEPARTMENT_')
			);

			if (departmentSelection) {
				isDepartment = true;
				_assignedUser = departmentSelection.replace('DEPARTMENT_', '');
			} else {
				_assignees = selectedValues
					.filter((v) => v.startsWith('USER_'))
					.map((v) => v.replace('USER_', ''));

				if (_assignees.length === 0) {
					_assignedUser = 'Unassigned';
				}
			}

			const res = await axios.post(
				`${getWebchatUrl(
					window.location.href
				)}/api/team/bulk-assign-conversations`,
				{
					_customerConversations: selectedConversations.map(
						(conversation: any) => conversation._id
					),
					_assignees: _assignees,
					_assignedUser: _assignedUser,
					isDepartment: isDepartment,
					_business: selectedConversations[0]._business,
				}
			);

			if (!res.data.error) {
				toast.success('Conversations assigned');
			} else {
				toast.error('Error assigning conversations');
			}
		});
	};

	const assigneeOptions = useMemo(() => {
		const userOptions = assignees
			.filter((a) => a._id !== 'UNASSIGNED')
			.map((assignee) => ({
				label: assignee.name,
				value: `USER_${assignee._id}`,
			}));

		const departmentOptions = allowedDepartments.map((department) => ({
			label: department.name,
			value: `DEPARTMENT_${department._id}`,
		}));

		return [
			{
				heading: 'Users',
				options: userOptions,
			},
			{
				heading: 'Departments',
				options: departmentOptions,
			},
		];
	}, [assignees, allowedDepartments]);

	return {
		selectedConversations,
		conversations,
		folders,
		tags,
		convertedTags,
		assignees,
		allowedDepartments,
		assigneeOptions,
		selectedTags,
		setSelectedTags,
		selectedFolder,
		isLoading,
		shouldShowOpenOrClosed,
		getFolderTitle,
		onClear,
		onSelectAll,
		onMarkRead,
		onMarkUnread,
		onMarkOpen,
		onMarkClosed,
		onAddAssigneeToConversations,
		onAddAssigneesToConversations,
		onMoveConversationsToFolder,
		onAddTagToConversations,
	};
};

export const ActionBar = () => {
	const {
		selectedConversations,
		conversations,
		folders,
		convertedTags,
		assigneeOptions,
		selectedTags,
		setSelectedTags,
		selectedFolder,
		isLoading,
		shouldShowOpenOrClosed,
		getFolderTitle,
		onClear,
		onSelectAll,
		onMarkRead,
		onMarkUnread,
		onMarkOpen,
		onMarkClosed,
		onAddAssigneesToConversations,
		onMoveConversationsToFolder,
		onAddTagToConversations,
	} = useBulkConversationActions();

	return (
		<motion.div
			initial={{ opacity: 0, y: -10 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -10 }}
			transition={{
				duration: 0.3,
				ease: [0.4, 0.0, 0.2, 1],
			}}
			// 768-1279px (landscape phones, iPads): the list is about 280px wide and
			// may be short, so the bulk controls sit on one row of icons.
			className="flex flex-col border-b px-2 pt-2 pb-4 gap-2 max-xl:gap-1.5 max-xl:pb-2"
		>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="font-medium text-base">
						{selectedConversations.length} selected
					</span>
					{isLoading && (
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<Loader2 className="size-4 animate-spin" />
							<span>Processing...</span>
						</div>
					)}
				</div>

				<div className="flex flex-row gap-2">
					<Button
						variant="outline"
						className=""
						size="sm"
						onClick={onSelectAll}
						disabled={isLoading}
					>
						{selectedConversations.length === conversations.length
							? 'Deselect All'
							: 'Select All'}
					</Button>

					<Button
						variant="outline"
						size="sm"
						onClick={onClear}
						disabled={isLoading}
					>
						Clear
					</Button>
				</div>
			</div>
			<div className="flex flex-col gap-2 w-full max-xl:flex-row max-xl:flex-wrap max-xl:gap-1">
				<div className="flex items-center flex-wrap gap-2 w-full max-xl:w-auto max-xl:gap-1">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								className="flex items-center gap-2 h-[40px] max-xl:h-9 max-xl:gap-1 max-xl:px-2"
								disabled={isLoading}
							>
								<CheckCheck className="w-4 h-4" />
								<span className="max-xl:sr-only">Mark as</span>
								<ChevronDown className="w-4 h-4 opacity-50" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start">
							<DropdownMenuItem
								onSelect={onMarkOpen}
								disabled={isLoading || shouldShowOpenOrClosed}
								className="flex items-center gap-2 cursor-pointer"
							>
								<FolderOpen className="w-4 h-4" />
								Open
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={onMarkClosed}
								disabled={isLoading || !shouldShowOpenOrClosed}
								className="flex items-center gap-2 cursor-pointer"
							>
								<CircleCheck className="w-4 h-4" />
								Closed
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={onMarkRead}
								disabled={isLoading}
								className="flex items-center gap-2 cursor-pointer"
							>
								<MailCheck className="w-4 h-4" />
								Read
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={onMarkUnread}
								disabled={isLoading}
								className="flex items-center gap-2 cursor-pointer"
							>
								<MailWarning className="w-4 h-4" />
								Unread
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>

					<Tooltip delayDuration={0}>
						<TooltipTrigger asChild>
							<div className="flex items-center">
								<MultiSelect
									key={selectedConversations
										.map((c: any) => c._id)
										.join(',')}
									options={assigneeOptions}
									onValueChange={
										onAddAssigneesToConversations
									}
									placeholder="Assign to"
									placeholderIcon={
										<User className="size-4 text-muted-foreground" />
									}
									className={MEDIUM_ICON_MULTI_SELECT_CLASS + ' min-w-[140px] h-[40px] transition-none'}
									hideSelectAll
									closeOnSelect
									maxCount={1}
									disabled={isLoading}
									animationConfig={{
										badgeAnimation: 'none',
										duration: 0,
										delay: 0,
										popoverAnimation: 'none',
										optionHoverAnimation: 'none',
									}}
								/>
							</div>
						</TooltipTrigger>
						<TooltipContent>Assign conversations</TooltipContent>
					</Tooltip>
				</div>
				<div className="flex gap-2 flex-wrap max-xl:gap-1">
					<Select
						value={selectedFolder}
						onValueChange={onMoveConversationsToFolder}
						disabled={isLoading}
					>
						<SelectTrigger
							className="flex-1 flex items-center gap-2 h-[40px] max-xl:h-9 max-xl:w-auto max-xl:flex-none max-xl:gap-1 max-xl:px-2"
						>
							<div className="flex items-center gap-2">
								<Folder className="w-4 h-4" />
								<span className="max-xl:sr-only">
									{selectedFolder
										? getFolderTitle(selectedFolder)
										: 'Move to Folder'}
								</span>
							</div>
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								<SelectLabel>Folders</SelectLabel>
								{folders.length === 0 ? (
									<SelectItem disabled value="no-folders">
										No folders
									</SelectItem>
								) : (
									folders.map((folder) => (
										<SelectItem
											className="cursor-pointer"
											key={folder._id || folder}
											value={folder._id || folder}
										>
											{folder.title || folder}
										</SelectItem>
									))
								)}
							</SelectGroup>
						</SelectContent>
					</Select>
					<MultiSelect
						className={MEDIUM_ICON_MULTI_SELECT_CLASS + ' text-nowrap text-ellipsis shadow-sm text-foreground font-normal'}
						options={convertedTags}
						defaultValue={selectedTags}
						onValueChange={setSelectedTags}
						placeholder="Add a tag"
						variant="default"
						handleSubmit={() => onAddTagToConversations()}
						allowCreate
						allowUnlistedSelection
						onCreateOption={(newTag) =>
							onAddTagToConversations(newTag)
						}
						createOptionLabel={(input) => `Create tag "${input}"`}
						placeholderIcon={<Tag className="w-4 h-4" />}
						animationConfig={{
							badgeAnimation: 'none',
							duration: 0,
							delay: 0,
							popoverAnimation: 'none',
							optionHoverAnimation: 'none',
						}}
					/>
				</div>
			</div>
		</motion.div>
	);
};
